import { Link, useRouterState } from '@tanstack/react-router';
import {
  Calendar,
  Gamepad2,
  Home,
  Image,
  LineChart,
  Settings,
  UserPlus,
  Users,
  UsersRound,
} from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';
import { cn } from '@/lib/utils';

interface NavigationProps {
  readonly className?: string;
}

interface ListItemProps {
  readonly title: string;
  readonly description?: string;
  readonly icon?: React.ComponentType<{ className?: string }>;
  readonly href: string;
}

const ListItem = ({ title, description, icon: Icon, href }: ListItemProps) => {
  return (
    <li>
      <NavigationMenuLink asChild>
        <Link
          to={href}
          className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
        >
          <div className="flex items-center gap-2">
            {Icon && <Icon className="h-4 w-4 shrink-0" />}
            <div className="text-sm font-medium leading-none">{title}</div>
          </div>
          {description && (
            <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">{description}</p>
          )}
        </Link>
      </NavigationMenuLink>
    </li>
  );
};

export function Navigation({ className }: NavigationProps) {
  const { t } = useTranslation('common');
  const router = useRouterState();
  const currentPath = router.location.pathname;

  const isActive = (path: string) => {
    if (path === '/') return currentPath === '/';
    return currentPath.startsWith(path);
  };

  return (
    <NavigationMenu className={cn('justify-start', className)}>
      <NavigationMenuList className="justify-start">
        {/* Games Group */}
        <NavigationMenuItem>
          <NavigationMenuTrigger
            className={cn(
              isActive('/sessions') || isActive('/games') || isActive('/templates')
                ? 'bg-accent text-accent-foreground'
                : '',
            )}
          >
            {t('nav.games.group', 'Games')}
          </NavigationMenuTrigger>
          <NavigationMenuContent>
            <ul className="grid gap-2 md:w-[400px] lg:w-[500px] lg:grid-cols-[.75fr_1fr]">
              <li className="row-span-3">
                <NavigationMenuLink asChild>
                  <Link
                    to="/sessions"
                    className={cn(
                      'from-muted/50 to-muted flex h-full w-full flex-col justify-end rounded-md bg-gradient-to-b p-4 no-underline outline-none transition-all duration-200 select-none focus:shadow-md md:p-6',
                      isActive('/sessions') && 'bg-accent',
                    )}
                  >
                    <Calendar className="mb-2 h-6 w-6" />
                    <div className="mb-2 text-lg font-medium sm:mt-4">
                      {t('nav.sessions', 'Sessions')}
                    </div>
                    <p className="text-muted-foreground text-sm leading-tight">
                      {t('sessions.description', 'Manage and track your game sessions')}
                    </p>
                  </Link>
                </NavigationMenuLink>
              </li>
              <ListItem
                href="/games"
                title={t('nav.games', 'Games')}
                description={t('nav.games.description', 'Game definitions and presets')}
                icon={Gamepad2}
              />
              <ListItem
                href="/templates"
                title={t('nav.templates', 'Templates')}
                description={t('nav.templates.description', 'Session templates for quick setup')}
                icon={Calendar}
              />
            </ul>
          </NavigationMenuContent>
        </NavigationMenuItem>

        {/* Players Group */}
        <NavigationMenuItem>
          <NavigationMenuTrigger
            className={cn(
              isActive('/players') || isActive('/player-groups') || isActive('/team-presets')
                ? 'bg-accent text-accent-foreground'
                : '',
            )}
          >
            {t('nav.players.group', 'Players')}
          </NavigationMenuTrigger>
          <NavigationMenuContent>
            <ul className="grid gap-2 md:w-[400px] lg:w-[500px] lg:grid-cols-[.75fr_1fr]">
              <li className="row-span-3">
                <NavigationMenuLink asChild>
                  <Link
                    to="/players"
                    className={cn(
                      'from-muted/50 to-muted flex h-full w-full flex-col justify-end rounded-md bg-gradient-to-b p-4 no-underline outline-none transition-all duration-200 select-none focus:shadow-md md:p-6',
                      isActive('/players') && 'bg-accent',
                    )}
                  >
                    <Users className="mb-2 h-6 w-6" />
                    <div className="mb-2 text-lg font-medium sm:mt-4">
                      {t('nav.players', 'Players')}
                    </div>
                    <p className="text-muted-foreground text-sm leading-tight">
                      {t('players.description', 'Manage your players')}
                    </p>
                  </Link>
                </NavigationMenuLink>
              </li>
              <ListItem
                href="/player-groups"
                title={t('nav.groups', 'Groups')}
                description={t(
                  'playerGroups.description',
                  'Collect players into reusable groups for faster session setup.',
                )}
                icon={UserPlus}
              />
              <ListItem
                href="/team-presets"
                title={t('nav.teams', 'Teams')}
                description={t('teams.description', 'Manage your team presets')}
                icon={UsersRound}
              />
            </ul>
          </NavigationMenuContent>
        </NavigationMenuItem>

        {/* App Group */}
        <NavigationMenuItem>
          <NavigationMenuTrigger
            className={cn(
              isActive('/analytics') ||
                isActive('/') ||
                isActive('/settings') ||
                isActive('/images')
                ? 'bg-accent text-accent-foreground'
                : '',
            )}
          >
            {t('nav.app.group', 'App')}
          </NavigationMenuTrigger>
          <NavigationMenuContent>
            <ul className="grid gap-2 md:w-[400px] lg:w-[500px] lg:grid-cols-[.75fr_1fr]">
              <li className="row-span-3">
                <NavigationMenuLink asChild>
                  <Link
                    to="/analytics"
                    className={cn(
                      'from-muted/50 to-muted flex h-full w-full flex-col justify-end rounded-md bg-gradient-to-b p-4 no-underline outline-none transition-all duration-200 select-none focus:shadow-md md:p-6',
                      isActive('/analytics') && 'bg-accent',
                    )}
                  >
                    <LineChart className="mb-2 h-6 w-6" />
                    <div className="mb-2 text-lg font-medium sm:mt-4">
                      {t('nav.analytics', 'Analytics')}
                    </div>
                    <p className="text-muted-foreground text-sm leading-tight">
                      {t('nav.analytics.description', 'Track your game statistics and insights')}
                    </p>
                  </Link>
                </NavigationMenuLink>
              </li>
              <ListItem
                href="/"
                title={t('nav.home', 'Home')}
                description={t('nav.home.description', 'One Score to rule them all')}
                icon={Home}
              />
              <ListItem
                href="/images"
                title={t('nav.images', 'Gallery')}
                description={t('nav.images.description', 'Browse and manage uploaded images')}
                icon={Image}
              />
              <ListItem
                href="/settings"
                title={t('settings.title', 'Settings')}
                description={t('settings.description', 'Manage your app preferences and data')}
                icon={Settings}
              />
            </ul>
          </NavigationMenuContent>
        </NavigationMenuItem>
      </NavigationMenuList>
    </NavigationMenu>
  );
}
